<script setup lang="ts">
import { computed } from 'vue'
import { ClipboardDocumentListIcon, ChatBubbleLeftIcon } from '@heroicons/vue/24/outline'
import { highlight } from '@/utils/highlight'
import type { OsOrderResult } from '@/api/omnisearchApi'

const props = defineProps<{
  item: OsOrderResult
  query: string
}>()

const emit = defineEmits<{ navigate: [] }>()

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Brouillon',
  SENT: 'Envoyée',
  IN_PROGRESS: 'En cours',
  VALIDATED: 'Validée',
  REFUSED: 'Refusée',
  CANCELLED: 'Annulée',
}

const STATUS_COLORS: Record<string, string> = {
  DRAFT: 'badge-ghost',
  SENT: 'badge-info',
  IN_PROGRESS: 'badge-warning',
  VALIDATED: 'badge-success',
  REFUSED: 'badge-error',
  CANCELLED: 'badge-ghost',
}

function handleClick() {
  emit('navigate')
  window.location.href = `/dashboard/order/${props.item.uuid}`
}

// Commentaires liés qui contiennent le terme recherché
const matchingComments = computed(() => {
  const q = props.query.trim().toLowerCase()
  if (!q || !props.item.orderComments?.length) return []
  return props.item.orderComments.filter((c) =>
    c.comment.toLowerCase().includes(q),
  )
})

// Champs qui ont déclenché le match (pour les tags)
const matchedIn = computed(() => {
  const q = props.query.trim().toLowerCase()
  if (!q) return []
  const fields: string[] = []
  if (props.item.quote?.quoteReference?.toLowerCase().includes(q)) fields.push('référence')
  if (props.item.quote?.company?.name?.toLowerCase().includes(q)) fields.push('client')
  if (props.item.comments?.toLowerCase().includes(q)) fields.push('commentaire')
  if (matchingComments.value.length) fields.push('historique')
  return fields
})
</script>

<template>
  <button
    class="w-full flex items-start gap-3 px-3 py-2.5 rounded-lg hover:bg-base-200 transition-colors text-left group cursor-pointer"
    @click="handleClick"
  >
    <span class="mt-0.5 flex-shrink-0 w-8 h-8 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
      <ClipboardDocumentListIcon class="w-4 h-4 text-amber-600 dark:text-amber-400" />
    </span>

    <div class="flex-1 min-w-0 space-y-0.5">

      <!-- Ligne 1 : référence devis + statut -->
      <div class="flex items-center gap-2 min-w-0">
        <p class="text-sm font-medium truncate text-base-content flex-1 min-w-0">
          <span v-html="highlight(item.quote?.quoteReference ?? item.uuid, query)" />
        </p>
        <span :class="['badge badge-xs flex-shrink-0', STATUS_COLORS[item.status] ?? 'badge-ghost']">
          {{ STATUS_LABELS[item.status] ?? item.status }}
        </span>
      </div>

      <!-- Ligne 2 : nom du client (highlighté) -->
      <p
        v-if="item.quote?.company?.name"
        class="text-xs text-base-content/60 truncate"
        v-html="highlight(item.quote.company.name, query)"
      />

      <!-- Ligne 3 : commentaire direct de la commande (si match) -->
      <p
        v-if="item.comments && matchedIn.includes('commentaire')"
        class="text-xs text-base-content/50 truncate max-w-sm"
        v-html="highlight(item.comments, query)"
      />

      <!-- Ligne 4 : extraits des commentaires d'historique (si match) -->
      <div v-if="matchingComments.length" class="space-y-0.5">
        <div
          v-for="c in matchingComments"
          :key="c.uuid"
          class="flex items-start gap-1 text-xs text-base-content/50"
        >
          <ChatBubbleLeftIcon class="w-3 h-3 mt-0.5 flex-shrink-0 text-base-content/30" />
          <span
            class="truncate max-w-xs"
            v-html="highlight(c.comment, query)"
          />
        </div>
      </div>

      <!-- Tags de match -->
      <div v-if="matchedIn.length" class="flex flex-wrap gap-1 pt-0.5">
        <span
          v-for="field in matchedIn"
          :key="field"
          class="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
        >
          {{ field }}
        </span>
      </div>

    </div>

    <span class="text-xs text-base-content/40 self-start mt-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">→</span>
  </button>
</template>
